import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

const NAV_KEYS = [
  { key: "nav_home", href: "#home" },
  { key: "nav_about", href: "#about" },
  { key: "nav_services", href: "#services" },
  { key: "nav_flow", href: "#flow" },
  { key: "nav_testimonials", href: "#testimonials" },
  { key: "nav_faq", href: "#faq" },
  { key: "nav_contact", href: "#contact" },
];

const sectionIds = NAV_KEYS.map((item) => item.href.replace("#", ""));

const LANGS = [
  { code: "ja", label: "JP", name: "日本語" },
  { code: "en", label: "EN", name: "English" },
  { code: "zh", label: "ZH", name: "简体中文" },
  { code: "zh-TW", label: "TW", name: "繁體中文" },
  { code: "ko", label: "KO", name: "한국어" },
  { code: "fr", label: "FR", name: "Français" },
  { code: "de", label: "DE", name: "Deutsch" },
  { code: "es", label: "ES", name: "Español" },
  { code: "it", label: "IT", name: "Italiano" },
  { code: "pt", label: "PT", name: "Português" },
  { code: "ru", label: "RU", name: "Русский" },
  { code: "ar", label: "AR", name: "العربية" },
  { code: "hi", label: "HI", name: "हिन्दी" },
  { code: "th", label: "TH", name: "ภาษาไทย" },
  { code: "vi", label: "VI", name: "Tiếng Việt" },
  { code: "id", label: "ID", name: "Indonesia" },
  { code: "tl", label: "TL", name: "Filipino" },
  { code: "ne", label: "NE", name: "नेपाली" },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [langOpen, setLangOpen] = useState(false);
  const [mobileLangOpen, setMobileLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
      let current = "home";
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 100) current = id;
        }
      }
      setActiveSection(current);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ドロップダウン外クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNav = (href: string) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const switchLang = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
    setMobileLangOpen(false);
  };

  const currentLang = LANGS.find((l) => l.code === i18n.language) ?? LANGS[0];

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          scrolled
            ? "bg-white/90 backdrop-blur-xl border-b border-gray-100/80"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-[72px]">

          {/* Logo */}
          <a
            href="#home"
            onClick={(e) => { e.preventDefault(); handleNav("#home"); }}
            className="flex items-center gap-3 cursor-pointer flex-shrink-0"
          >
            <img
              src="https://public.readdy.ai/ai/img_res/edited_3693c096081961b489aaeeec35ce6ce4_ea61dad8.jpg"
              alt="てんてんおじさん鑑定所ロゴ"
              className={`w-auto object-contain transition-all duration-500 ${scrolled ? "h-10" : "h-12"}`}
            />
            <div className="hidden lg:block">
              <p
                className={`text-[10px] font-medium tracking-[0.25em] transition-colors duration-300 ${
                  scrolled ? "text-[#0ABAB5]" : "text-white/80"
                }`}
              >
                {t("nav_subtitle")}
              </p>
              <p
                className={`text-sm font-bold leading-tight tracking-wide transition-colors duration-300 ${
                  scrolled ? "text-gray-800" : "text-white"
                }`}
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                {t("nav_title")}
              </p>
            </div>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_KEYS.map((item) => {
              const isActive = activeSection === item.href.replace("#", "");
              return (
                <button
                  key={item.href}
                  onClick={() => handleNav(item.href)}
                  className={`relative px-3 py-2 text-xs font-medium tracking-wide transition-all duration-200 whitespace-nowrap cursor-pointer rounded-lg ${
                    scrolled
                      ? isActive
                        ? "text-[#0ABAB5]"
                        : "text-gray-600 hover:text-[#0ABAB5] hover:bg-[#0ABAB5]/5"
                      : isActive
                        ? "text-white"
                        : "text-white/75 hover:text-white hover:bg-white/10"
                  }`}
                >
                  {t(item.key)}
                  <span
                    className={`absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full transition-all duration-300 ${
                      isActive
                        ? scrolled ? "bg-[#0ABAB5] opacity-100" : "bg-white opacity-100"
                        : "opacity-0"
                    }`}
                  />
                </button>
              );
            })}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-2">

            {/* Desktop Language Dropdown */}
            <div ref={langRef} className="hidden lg:block relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    : "bg-white/15 backdrop-blur-sm text-white hover:bg-white/25"
                }`}
              >
                <i className="ri-global-line text-sm" />
                {currentLang.label}
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${langOpen ? "rotate-180" : ""}`} />
              </button>

              {/* Dropdown panel — 2-column grid */}
              <div
                className={`absolute right-0 top-full mt-2 bg-white rounded-2xl border border-gray-100 overflow-hidden transition-all duration-200 origin-top-right ${
                  langOpen
                    ? "opacity-100 scale-100 pointer-events-auto"
                    : "opacity-0 scale-95 pointer-events-none"
                }`}
                style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.08)", width: "272px" }}
              >
                <div className="p-2 grid grid-cols-2 gap-1">
                  {LANGS.map((lang) => {
                    const isActive = i18n.language === lang.code;
                    return (
                      <button
                        key={lang.code}
                        onClick={() => switchLang(lang.code)}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs transition-colors duration-150 cursor-pointer ${
                          isActive
                            ? "bg-[#e8f9f8] text-[#0ABAB5] font-semibold"
                            : "text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        <span className={`font-bold w-6 flex-shrink-0 ${isActive ? "text-[#0ABAB5]" : "text-gray-400"}`}>
                          {lang.label}
                        </span>
                        <span className="truncate">{lang.name}</span>
                        {isActive && <i className="ri-check-line text-[#0ABAB5] text-xs ml-auto flex-shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <button
              onClick={() => handleNav("#contact")}
              className={`hidden lg:flex items-center gap-2 text-xs font-semibold px-5 py-2.5 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer ${
                scrolled
                  ? "bg-[#0ABAB5] text-white hover:bg-[#089e99]"
                  : "border border-white/60 text-white hover:bg-white/15 backdrop-blur-sm"
              }`}
            >
              {t("nav_cta")}
              <i className="ri-arrow-right-line text-xs" />
            </button>

            {/* Hamburger */}
            <button
              className={`lg:hidden w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer transition-all duration-200 ${
                scrolled
                  ? "text-gray-700 hover:bg-gray-100"
                  : "text-white hover:bg-white/15"
              }`}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="メニュー"
            >
              <i className={`text-lg transition-all duration-200 ${menuOpen ? "ri-close-line" : "ri-menu-3-line"}`} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${
          menuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setMenuOpen(false)}
        style={{ background: "rgba(0,0,0,0.3)", backdropFilter: "blur(4px)" }}
      />

      {/* Mobile Menu Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-72 z-50 lg:hidden bg-white transition-transform duration-400 ease-out flex flex-col`}
        style={{
          transform: menuOpen ? "translateX(0)" : "translateX(100%)",
          transitionDuration: "350ms",
        }}
      >
        {/* Panel header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div>
            <p className="text-[10px] text-[#0ABAB5] tracking-widest font-medium">{t("nav_subtitle")}</p>
            <p className="text-sm font-bold text-gray-800" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              {t("nav_title")}
            </p>
          </div>
          <button
            onClick={() => setMenuOpen(false)}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Mobile Language Dropdown */}
        <div className="px-4 pt-4 pb-2">
          <button
            onClick={() => setMobileLangOpen(!mobileLangOpen)}
            className="flex items-center gap-2 bg-gray-100 rounded-full px-4 py-2 text-xs font-bold text-gray-600 cursor-pointer hover:bg-gray-200 transition-colors w-fit"
          >
            <i className="ri-global-line text-sm text-[#0ABAB5]" />
            <span>{currentLang.label}</span>
            <span className="text-gray-400">·</span>
            <span className="font-normal text-gray-500">{currentLang.name}</span>
            <i className={`ri-arrow-down-s-line text-sm text-gray-400 transition-transform duration-200 ${mobileLangOpen ? "rotate-180" : ""}`} />
          </button>

          {/* Mobile dropdown list — 2-column grid */}
          <div
            className={`mt-2 bg-white rounded-2xl border border-gray-100 overflow-hidden transition-all duration-200 ${
              mobileLangOpen ? "max-h-80 opacity-100" : "max-h-0 opacity-0"
            }`}
            style={{ boxShadow: mobileLangOpen ? "0 4px 16px rgba(0,0,0,0.06)" : "none" }}
          >
            <div className="p-2 grid grid-cols-2 gap-1">
              {LANGS.map((lang) => {
                const isActive = i18n.language === lang.code;
                return (
                  <button
                    key={lang.code}
                    onClick={() => switchLang(lang.code)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs transition-colors duration-150 cursor-pointer ${
                      isActive
                        ? "bg-[#e8f9f8] text-[#0ABAB5] font-semibold"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <span className={`font-bold w-6 flex-shrink-0 ${isActive ? "text-[#0ABAB5]" : "text-gray-400"}`}>
                      {lang.label}
                    </span>
                    <span className="truncate">{lang.name}</span>
                    {isActive && <i className="ri-check-line text-[#0ABAB5] text-xs ml-auto flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Nav links */}
        <nav className="flex-1 px-4 py-4 flex flex-col gap-1 overflow-y-auto">
          {NAV_KEYS.map((item) => {
            const isActive = activeSection === item.href.replace("#", "");
            return (
              <button
                key={item.href}
                onClick={() => handleNav(item.href)}
                className={`text-left px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer flex items-center gap-3 ${
                  isActive
                    ? "bg-[#e8f9f8] text-[#0ABAB5]"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full flex-shrink-0 transition-colors ${
                    isActive ? "bg-[#0ABAB5]" : "bg-gray-200"
                  }`}
                />
                {t(item.key)}
              </button>
            );
          })}
        </nav>

        {/* CTA */}
        <div className="px-4 pb-8 pt-2">
          <button
            onClick={() => handleNav("#contact")}
            className="w-full bg-[#0ABAB5] text-white text-sm font-semibold py-3.5 rounded-full hover:bg-[#089e99] transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
          >
            {t("nav_cta")}
            <i className="ri-arrow-right-line" />
          </button>
        </div>
      </div>
    </>
  );
}
